<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <router-link :to="{ name: 'CashBook' }" class="mb-0 main-identifier fw-semibold text-decoration-none"
        title="Go back to Cashbook">
        Cashbook
      </router-link>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">View Entry</p>
    </div>

    <div class="d-flex justify-content-center">
      <div class="form-outer" style="max-width: 100%;">
        <div class="view-item position-relative">

          <div class="form-inner">
            <div class="form-header d-flex justify-content-between align-items-center report">
              <div>
                <h1 class="mb-0">Cashbook Entry</h1>
              </div>
            </div>

            <div class="form-body cashbook-content-area position-relative">
              <div v-if="loadingCashbookEntry" class="loading-overlay text-center">
                <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                <p class="mt-2 mb-0 text-muted">Loading Cashbook Entry information...</p>
              </div>

              <div v-if="errorMessage && !loadingCashbookEntry" class="alert alert-warning" role="alert">
                {{ errorMessage }}
              </div>

              <div class="filters-section mb-4">
                <div class="row col-md-12 g-3">
                  <div v-if="hasInflowBeenUpdated" class="col-md-3">
                    <label for="inflow" class="form-label small fw-semibold">Inflow:</label>
                    <input id="inflow" type="text" class="form-control view-only-input"
                      title="Cannot edit submitted Inflow" :value="formatCurrency(savedInflow)" readonly />
                  </div>

                  <div v-else class="row">
                    <div class="col-md-3">
                      <label for="inflow" class="form-label small fw-semibold">Enter Inflow:</label>
                      <input id="inflow" type="text" class="form-control" :value="formatCurrency(form.inflow)"
                        inputmode="numeric" pattern="[0-9,.]*" @keypress="onlyDigitsAndComma" @input="onInflowInput"
                        placeholder="0.00" required />
                    </div>

                    <div class="col-md-2 d-flex align-items-center">
                      <div class="w-100">
                        <label class="form-label small fw-semibold invisible">Action</label>

                        <button class="btn btn-sm btn-outline-secondary w-100" @click="openInflowConfirmationModal">
                          Submit
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Table Controls -->
              <div class="table-controls">
                <div class="table-controls-top">
                  <div class="table-controls-left">
                    <div class="entries-control">
                      <span>Show</span>
                      <select class="form-select form-select-sm" v-model.number="pageLength">
                        <option :value="10">10</option>
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                      </select>
                      <span>Entries</span>
                    </div>

                  </div>

                  <div class="export-wrapper"
                    :title="paginatedItems.length === 0 ? 'No data to export' : 'Export current view to Excel'">
                    <button class="btn btn-md btn-success w-100 d-flex justify-content-between align-items-center"
                      @click="exportData" :disabled="paginatedItems.length === 0">
                      <span class="material-symbols-outlined align-items-center me-2">download</span>
                      Export
                    </button>
                  </div>
                </div>

              </div>

              <!-- Table -->
              <div class="table-wrapper">
                <div class="table-container-wide">
                  <table class="cashbook-table" style="width: 100%; white-space: nowrap;">
                    <thead>
                      <tr>
                        <th rowspan="2">DATE</th>
                        <th rowspan="2">PAYEE</th>
                        <th rowspan="2">PARTICULARS</th>
                        <th rowspan="2">TYPE</th>
                        <th rowspan="2">BANK</th>
                        <th rowspan="2" class="amount-col">INFLOW</th>
                        <th rowspan="2" class="amount-col">GROSS</th>
                        <th rowspan="2" class="amount-col">OUTFLOW (NET)</th>
                        <th rowspan="2" class="amount-col">BALANCE</th>
                        <th colspan="6" class="deductions-header">DEDUCTIONS</th>
                      </tr>
                      <tr>
                        <th class="amount-col">ADMIN CHARGE</th>
                        <th class="amount-col">FG W/T</th>
                        <th class="amount-col">VAT</th>
                        <th class="amount-col">STATE W/T</th>
                        <th class="amount-col">OTHERS</th>
                        <th class="amount-col">TOTAL</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="paginatedItems.length === 0">
                        <td colspan="15" class="text-center text-muted">No transaction to display.</td>
                      </tr>
                      <tr v-for="(transaction, index) in paginatedItems" :key="transaction.id"
                        :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                        <td>{{ formatDate(transaction.createdDate) }}</td>
                        <td>{{ transaction.payee }}</td>
                        <td>{{ transaction.particulars }}</td>
                        <td>{{ transaction.natureOfExpenditure }}</td>
                        <td>{{ transaction.bankName }}</td>
                        <td class="amount-col">{{ formatCurrency(transaction.inflow) }}</td>
                        <td class="amount-col">{{ formatCurrency(transaction.grossAmount) }}</td>
                        <td class="amount-col">{{ formatCurrency(transaction.netOutflow) }}</td>
                        <td class="amount-col fw-semibold">{{ formatCurrency(transaction.balance) }}</td>
                        <td class="amount-col">{{ formatCurrency(deductionMap.admin) }}</td>
                        <td class="amount-col">{{ formatCurrency(deductionMap.fgwt) }}</td>
                        <td class="amount-col">{{ formatCurrency(deductionMap.vat) }}</td>
                        <td class="amount-col">{{ formatCurrency(deductionMap.statewt) }}</td>
                        <td class="amount-col">{{ formatCurrency(deductionMap.others) }}</td>
                        <td class="amount-col fw-semibold">{{ formatCurrency(transaction.totalDeduction) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Pagination Summary and Actions -->
              <div class="pagination-info">
                <small>
                  Showing {{ (currentPage - 1) * pageLength + 1 }} to
                  {{ Math.min(currentPage * pageLength, filteredItems.length) }} of
                  {{ filteredItems.length }} entries
                </small>

                <!-- Pagination Controls -->
                <nav class="pagination-nav">
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <button class="page-link" @click.prevent="changePage(currentPage - 1)">Previous</button>
                    </li>
                    <li v-for="page in displayedPages" :key="page" class="page-item"
                      :class="{ active: currentPage === page }">
                      <button class="page-link" @click.prevent="changePage(page)">{{ page }}</button>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <button class="page-link" @click.prevent="changePage(currentPage + 1)">Next</button>
                    </li>
                  </ul>
                </nav>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showInflowConfirmationModal" class="inflow-modal-overlay" @click="closeInflowModal">
      <div class="inflow-modal" @click.stop>
        <div class="inflow-modal-header">
          <h5 class="mb-0">{{ inflowModalMode === 'edit' ? 'Edit Inflow' : 'Add Inflow' }}</h5>
          <button type="button" class="btn-close" aria-label="Close" @click="closeInflowModal"></button>
        </div>

        <div class="inflow-modal-body">
          <div class="mb-3">
            <label class="form-label mb-2">Are you sure you want to add this inflow?</label>
            <input type="number" class="form-control" v-model.number="form.inflow" :min="form.grossAmount" step="0.01"
              inputmode="decimal" @keydown="preventNonNumericInput" placeholder="Enter amount" />
          </div>

          <p class="inflow-hint mt-3 mb-0">
            Tip: Inflow cannot be changed after Submission.
          </p>
        </div>

        <div class="inflow-modal-footer">
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="closeInflowModal">Cancel</button>
          <button type="button" class="btn btn-outline-primary btn-sm" :disabled="Number(form.inflow || 0) <= 0"
            @click="submitInflow">
            {{ inflowModalMode === 'edit' ? 'Save Changes' : 'Add Inflow' }}
          </button>
        </div>
      </div>
    </div>

  </DashboardLayout>
</template>

<script setup>
import { ref, computed, onMounted, reactive, watch } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import DashboardLayout from '../layout/DashboardLayout.vue'
import { useRoute } from 'vue-router';
import { debounce } from 'lodash'
import ExcelJS from 'exceljs'
import Swal from 'sweetalert2'
import '@/assets/css/table.css'
import '@/assets/css/responsive-table.css'

const route = useRoute()
const transactions = ref([])
const mdas = ref([])
// const loading = ref(false)
const loadingCashbookEntry = ref(true)
const loadingBanks = ref(false)
const loadingMDAs = ref(false)
const banks = ref([])
const search = ref('')
const pageLength = ref(10)
const currentPage = ref(1)
const errorMessage = ref('')
const transactionId = route.params.id
const root_api_url = process.env.VUE_APP_API_ROOT_URL
const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`
const showInflowConfirmationModal = ref(false)
const savedInflow = ref(0)

const hasInflowBeenUpdated = computed(() => {
  return savedInflow.value > 0
})

const debouncedSearch = ref('')
watch(search, debounce((val) => {
  debouncedSearch.value = val
  currentPage.value = 1
}, 300))

watch(pageLength, () => {
  currentPage.value = 1
})


const form = reactive({
  id: '',
  createdBy: '',
  lastModifiedBy: '',
  userId: '',
  mdaId: '',
  bankId: '',
  payee: '',
  mdaName: '',
  bankName: '',
  particulars: '',
  natureOfExpenditure: '',
  warrantNumber: '',
  pvId: '',
  pvNumber: '',
  chequeNumber: '',
  inflow: '',
  netOutflow: '',
  balance: '',
  grossAmount: '',
  totalDeduction: '',
  deductions: {
    id: '',
    dedTypeId: '',
    dedTypeName: '',
    paymentVoucherId: '',
    amount: '',
    dedTypeAmt: '',
    dedTypeMode: '',
  },
  createdDate: '',
  lastModifiedDate: '',
  errorMessage: '',
})

const deductionItems = ref([])

const deductionMap = computed(() => {
  const map = {
    admin: 0,
    fgwt: 0,
    vat: 0,
    statewt: 0,
    others: 0
  }

  deductionItems.value.forEach(d => {
    const name = (d.dedTypeName || '').toLowerCase()

    if (name.includes('admin')) map.admin = d.amount
    else if (name.includes('fg')) map.fgwt = d.amount
    else if (name.includes('vat')) map.vat = d.amount
    else if (name.includes('state')) map.statewt = d.amount
    else map.others += d.amount
  })

  return map
})

const fetchEntry = async () => {
  const token = Cookies.get('auth_token')
  loadingCashbookEntry.value = true
  errorMessage.value = ''
  try {
    const response = await axios.get(`${root_api_url}/payment/cashbook/${transactionId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })

    const data = response.data.data

    form.id = data.id || ''
    form.createdBy = data.createdBy || ''
    form.lastModifiedBy = data.lastModifiedBy || ''
    form.userId = data.userId || ''
    form.mdaId = data.mdaId || ''
    form.bankId = data.bankId || ''
    form.payee = data.payee || ''
    form.mdaName = data.mdaName || ''
    form.bankName = data.bankName || ''
    form.particulars = data.particulars || ''
    form.natureOfExpenditure = data.natureOfExpenditure || ''
    form.warrantNumber = data.warrantNumber || ''
    form.pvId = data.pvId || ''
    form.pvNumber = data.pvNumber || ''
    form.chequeNumber = data.chequeNumber || ''
    form.inflow = Number(data.inflow || 0)
    savedInflow.value = Number(data.inflow || 0)
    form.netOutflow = data.netOutflow || ''
    form.balance = data.balance || ''
    form.grossAmount = data.grossAmount || ''
    form.totalDeduction = data.totalDeduction || ''
    form.createdDate = data.createdDate || ''
    form.lastModifiedDate = data.lastModifiedDate || ''

    deductionItems.value = (data.deductions || []).map(deduction => ({
      id: deduction.id || '',
      dedTypeId: deduction.dedTypeId || '',
      dedTypeName: deduction.dedTypeName || '',
      paymentVoucherId: deduction.paymentVoucherId || '',
      amount: deduction.amount || '',
      dedTypeAmt: deduction.dedTypeAmt || '',
      dedTypeMode: deduction.dedTypeMode || '',
    }))

    form.deductions = deductionItems.value

    console.log('Cashbook deduction items:', deductionItems.value);

    transactions.value = [data]

  } catch (error) {
    console.error('Failed to fetch transactions:', error)
    console.error('Error details:', error.response?.data) // Log the actual error
    errorMessage.value = error?.response?.data?.errorMessage || error?.response?.data?.error || error?.response?.data?.message || 'Failed to load cashbook entry. Please try again later.'
  } finally {
    loadingCashbookEntry.value = false
  }
}

const fetchMDAs = async () => {
  loadingMDAs.value = true

  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/eli/mda`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    const data = Array.isArray(response.data) ? response.data : []

    mdas.value = data.map(mda => ({
      id: mda.id,
      name: mda.name,
    }))
  } catch (error) {
    mdas.value = []
    console.error('Failed to fetch mdas:', error)
  } finally {
    loadingMDAs.value = false
  }
}

const fetchBanks = async () => {
  loadingBanks.value = true
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/payment/banks`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    const data = Array.isArray(response.data) ? response.data : []

    banks.value = data.map(bank => ({
      id: bank.id,
      name: bank.bankName || ''
    }))
  } catch (error) {
    banks.value = []
    console.error('Failed to fetch banks:', error)
  } finally {
    loadingBanks.value = false
  }
}

// const uniqueBanks = computed(() => {
//   return [...banks.value].sort((a, b) => a.name.localeCompare(b.name))
// })

const filteredItems = computed(() => {
  let filtered = transactions.value

  // Apply search filter
  if (debouncedSearch.value) {
    const term = debouncedSearch.value.toLowerCase()
    filtered = filtered.filter(transaction => {
      return (
        (transaction.mdaName || '').toLowerCase().includes(term) ||
        (transaction.payee || '').toLowerCase().includes(term) ||
        (transaction.particulars || '').toLowerCase().includes(term) ||
        (transaction.warrantNumber || '').toLowerCase().includes(term) ||
        (transaction.chequeNumber || '').toLowerCase().includes(term) ||
        (transaction.pvNumber || '').toLowerCase().includes(term)
      )
    })
  }

  return filtered
})

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * pageLength.value
  return filteredItems.value.slice(start, start + pageLength.value)
})

const totalPages = computed(() => {
  return Math.ceil(filteredItems.value.length / pageLength.value)
})

const displayedPages = computed(() => {
  const pages = []
  const maxDisplayed = 5
  let startPage = Math.max(1, currentPage.value - Math.floor(maxDisplayed / 2))
  let endPage = Math.min(totalPages.value, startPage + maxDisplayed - 1)

  if (endPage - startPage < maxDisplayed - 1) {
    startPage = Math.max(1, endPage - maxDisplayed + 1)
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i)
  }

  return pages
})

const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

// const clearFilters = () => {
//   filters.value = {
//     mda: '',
//     bank: '',
//   }
//   search.value = ''
// }

const openInflowConfirmationModal = () => {
  if (Number(form.inflow || 0) <= 0) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Please enter Inflow Amount before submitting.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      returnFocus: false,
      icon: undefined,
    }).then(() => {
      const inflowField = document.getElementById('inflow')
      if (inflowField) {
        inflowField.scrollIntoView({ behavior: 'smooth', block: 'center' })
        inflowField.focus()
      }
    })
    return
  }

  if (Number(form.inflow || 0) < form.grossAmount) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Inflow Amount must be equal to or greater than Gross Amount.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      returnFocus: false,
      icon: undefined,
    }).then(() => {
      const inflowField = document.getElementById('inflow')
      if (inflowField) {
        inflowField.scrollIntoView({ behavior: 'smooth', block: 'center' })
        inflowField.focus()
      }
    })
    return
  }
  if (loadingCashbookEntry.value) return

  showInflowConfirmationModal.value = true
}

const closeInflowModal = () => {
  showInflowConfirmationModal.value = false
}

const submitInflow = async () => {
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.patch(`${root_api_url}/payment/cashbook/${transactionId}/inflow`, null, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      params: {
        inflow: form.inflow
      }
    })
    console.log('Inflow update response:', response.data)
    closeInflowModal()
    await fetchEntry()
  }
  catch (error) {
    console.error('Failed to submit inflow:', error)
  }
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
}

const formatCurrency = (val) => {
  if (val === '' || val === null || val === undefined) return '';
  const num = Number(val);
  if (isNaN(num)) return '';
  return '₦' + num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function onlyDigitsAndComma(e) {
  const k = e.key;
  const allowed = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab', 'Home', 'End'];
  if ((k >= '0' && k <= '9') || allowed.includes(k)) return;
  e.preventDefault();
}

const preventNonNumericInput = (e) => {
  const key = e.key
  const allowed = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab', 'Home', 'End', '.']
  if ((key >= '0' && key <= '9') || allowed.includes(key)) {
    if (key !== '.') return

    const currentValue = String(e.target?.value || '')
    if (currentValue.includes('.')) e.preventDefault()
    return
  }

  e.preventDefault()
}

function onInflowInput(e) {
  // get only digits typed
  const raw = (e.target.value || '').replace(/\D/g, '');

  if (raw === '') {
    form.inflow = 0;
    e.target.value = '';
    return;
  }

  // last two digits are cents
  const num = parseInt(raw, 10) / 100;
  // keep numeric model with 2 decimals
  form.inflow = Number(num.toFixed(2));
  // show formatted string
  e.target.value = formatCurrency(form.inflow);
}

const exportData = async () => {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('Cashbook Entry')

  const headerFill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF157347' }
  }

  const headerFont = {
    bold: true,
    color: { argb: 'FFFFFFFF' }
  }

  // Title rows — merged A:S (19 cols), value on A (top-left of merge)
  worksheet.mergeCells('A1:S1')
  worksheet.mergeCells('A2:S2')

  worksheet.getCell('A1').value = 'OGUN STATE GOVERNMENT OF NIGERIA'
  worksheet.getCell('A2').value = 'PAYMENT VOUCHER CASHBOOK ENTRY: ' + form.pvNumber

  // Two-row column headers: rows 5–6
  // A–L: span both rows (merge 5:6)
  // M–S: DEDUCTIONS group in row 5, sub-headers in row 6
  const singleHeaders = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L']
  singleHeaders.forEach(col => {
    worksheet.mergeCells(`${col}3:${col}4`)
  })

  // DEDUCTIONS group header spans M3:S3
  worksheet.mergeCells('M3:S3')

  // Row 3 — main headers
  worksheet.getCell('A3').value = 'DATE'
  worksheet.getCell('B3').value = 'AGENCY'
  worksheet.getCell('C3').value = 'PAYEE'
  worksheet.getCell('D3').value = 'PARTICULARS'
  worksheet.getCell('E3').value = 'TYPE'
  worksheet.getCell('F3').value = 'R/WARRANT'
  worksheet.getCell('G3').value = 'PV NO'
  worksheet.getCell('H3').value = 'BANK'
  worksheet.getCell('I3').value = 'CHQ NO'
  worksheet.getCell('J3').value = 'INFLOW'
  worksheet.getCell('K3').value = 'OUTFLOW (NET)'
  worksheet.getCell('L3').value = 'BALANCE'
  worksheet.getCell('M3').value = 'DEDUCTIONS'

  // Row 4 — deduction sub-headers (start at col 13 = M)
  const deductionHeaders = [
    'ADMIN CHARGE', 'FG W/T', 'VAT', 'STATE W/T', 'OTHERS', 'GROSS', 'REMARK'
  ]
  deductionHeaders.forEach((header, index) => {
    worksheet.getCell(4, 13 + index).value = header
  })

  worksheet.properties.defaultRowHeight = 20
  worksheet.views = [{ state: 'frozen', ySplit: 4 }]
  worksheet.columns.forEach(col => { col.width = 20 })

  paginatedItems.value.forEach(transaction => {
    const row = worksheet.addRow([
      formatDate(transaction.createdDate),
      transaction.mdaName,
      transaction.payee,
      transaction.particulars,
      transaction.natureOfExpenditure,
      transaction.warrantNumber,
      transaction.pvNumber,
      transaction.bankName,
      transaction.chequeNumber,
      Number(transaction.inflow || 0),
      Number(transaction.netOutflow || 0),
      Number(transaction.balance || 0),
      Number(deductionMap.value.admin || 0),
      Number(deductionMap.value.fgwt || 0),
      Number(deductionMap.value.vat || 0),
      Number(deductionMap.value.statewt || 0),
      Number(deductionMap.value.others || 0),
      Number(transaction.grossAmount || 0),
      transaction.remark
    ])

    // Currency cols: J–R (10–18)
    const currencyCols = [10, 11, 12, 13, 14, 15, 16, 17, 18]
    currencyCols.forEach(col => {
      worksheet.getColumn(col).width = 20
      const cell = row.getCell(col)
      cell.value = Number(cell.value || 0)
      cell.numFmt = '#,##0.00'
      cell.alignment = { horizontal: 'right' }
    })
  })

  // Row heights
  worksheet.getRow(1).height = 25
  worksheet.getRow(2).height = 20
  worksheet.getRow(3).height = 20
  worksheet.getRow(4).height = 18

  // Header styles — rows 1–4, cols 1–19
  for (let row = 1; row <= 4; row++) {
    for (let col = 1; col <= 19; col++) {
      const cell = worksheet.getCell(row, col)
      cell.fill = headerFill
      cell.font = headerFont
      cell.alignment = { horizontal: 'center', vertical: 'middle' }
    }
  }

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  const filename = `${form.pvNumber}_cashbook_entry_${form.createdDate?.slice(0, 10)}`
  link.download = filename.toLocaleUpperCase() + '.xlsx'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

onMounted(async () => {
  await fetchMDAs()
  await fetchBanks()
  await fetchEntry()
  document.title = "Cashbook Entry"
})

</script>

<style scoped>
.view-only-input {
  background-color: transparent;
  cursor: default;
  pointer-events: none;
  color: #212529;
  font-size: 1rem;
  padding-right: 0;
  margin-right: 1rem;
  margin-bottom: 0.5rem !important;
}

.cashbook-content-area {
  min-height: 360px;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  pointer-events: all;
}

/* Fix horizontal scroll - override any parent overflow restrictions */
.view-item,
.form-inner,
.form-body {
  overflow: visible !important;
}

.form-outer {
  width: 100%;
  max-width: 100% !important;
}

/* Component-specific styles */
.primary-col {
  font-weight: 500;
  color: #212529;
}

.row-even {
  background-color: #ffffff;
}

.row-odd {
  background-color:var(--table-bg-head);
}

.material-symbols-outlined {
  font-size: 1.25rem;
}

.filters-section {
  background-color:var(--table-bg-head);
  padding: 1.5rem;
  border-radius: 0.375rem;
  border: 1px solid #dee2e6;
}

.form-label {
  margin-bottom: 0.25rem;
    color: var(--color);
}

/* Form Control Enhancements */
.form-control:focus,
.form-select:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.25);
}

.form-control[readonly] {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

.showing-info {
  padding-left: 0.5rem;
}

.export-wrapper {
  display: flex;
  align-items: center;
}

/* Wide table container for cashbook */
.table-container-wide {
  width: 100%;
  overflow-x: auto;
}

.cashbook-table {
  width: 100%;
  min-width: 1800px;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.cashbook-table thead {
  background-color:var(--table-bg-head);
  position: sticky;
  top: 0;
  z-index: 10;
}

.cashbook-table th {
  padding: 0.75rem 0.5rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.75rem;
  color: #1f2937;
  text-transform: uppercase;
  border: 1px solid #dee2e6;
  background-color:var(--table-bg-head);
  white-space: nowrap;
}

.cashbook-table th.deductions-header {
  text-align: center;
  background-color: var(--hover);
}

.cashbook-table td {
  padding: 0.75rem 0.5rem;
  border: 1px solid #dee2e6;
  color: #374151;
  white-space: nowrap;
}

.cashbook-table .amount-col {
  text-align: right;
}

.cashbook-table tbody tr:hover {
  background-color: #f3f4f6;
}

.text-center {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
}

.inflow-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1060;
  padding: 1rem;
}

.inflow-modal {
  background: #ffffff;
  width: min(560px, 100%);
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.inflow-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e5e7eb;
  padding: 0.85rem 1rem;
}

.inflow-modal-body {
  padding: 1rem;
}

.inflow-modal-footer {
  border-top: 1px solid #e5e7eb;
  padding: 0.85rem 1rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.inflow-input-mode {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.inflow-input-mode .mode-disabled {
  color: #9ca3af;
  opacity: 0.7;
}

.inflow-input-mode .mode-disabled span {
  color: #9ca3af;
}

.inflow-hint {
  color: #6b7280;
  font-size: 0.82rem;
}


/* Responsive adjustments */
@media (max-width: 768px) {
  .filters-section .row {
    gap: 0.75rem;
  }

  .table-controls-top {
    flex-direction: column;
    gap: 1rem;
  }
}
</style>
